import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Route as RouteIcon } from 'lucide-react';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import Skeleton from '../ui/Skeleton';
import { Table, TBody, TH, THead, TR, TD, TableScroller } from '../ui/Table';
import { getRouteStops } from '../../utils/api';

// Per-route stop table: the rows of the route_stops table for one route.
// Columns: #, Stop name, Stop ID, Route ID, RouteStopCode, Lat, Long.
export default function RouteStopsModal({ open, routeId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);

  const load = useCallback(async () => {
    if (!routeId) return;
    setLoading(true);
    try {
      const { data } = await getRouteStops(routeId);
      setRoute(data.route);
      setStops(data.stops);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load route stops');
    } finally {
      setLoading(false);
    }
  }, [routeId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={route ? route.routeName || `Route ${route.routeNumber}` : 'Route stops'}
      description={
        route
          ? `${route.routeId || 'No route ID'} · Route ${route.routeNumber} · ${route.direction} · ${route.stopCount} stops`
          : null
      }
      icon={RouteIcon}
      size="xl"
    >
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-9 rounded" />
          ))}
        </div>
      ) : stops.length === 0 ? (
        <p className="py-8 text-center text-[13px] text-slate-400">This route has no stops.</p>
      ) : (
        <TableScroller className="rounded-lg border border-slate-100">
          <Table>
            <THead>
              <TR hoverable={false}>
                <TH className="w-10">#</TH>
                <TH>Stop name</TH>
                <TH>Stop ID</TH>
                <TH>Route ID</TH>
                <TH>RouteStopCode</TH>
                <TH>Latitude</TH>
                <TH>Longitude</TH>
              </TR>
            </THead>
            <TBody>
              {stops.map((s) => (
                <TR key={s.id}>
                  <TD className="tabular-nums text-slate-400">{s.sequence}</TD>
                  <TD className="font-medium text-slate-900">{s.stopName}</TD>
                  <TD className="font-mono text-[12px] text-slate-600">{s.stopId}</TD>
                  <TD className="font-mono text-[12px] text-slate-500">{s.routeId || '—'}</TD>
                  <TD>
                    <Badge variant="brand" size="sm" className="font-mono tracking-wide">
                      {s.routeStopCode || '—'}
                    </Badge>
                  </TD>
                  <TD className="font-mono text-[12px] text-slate-500">{Number(s.lat).toFixed(6)}</TD>
                  <TD className="font-mono text-[12px] text-slate-500">{Number(s.lng).toFixed(6)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableScroller>
      )}
    </Modal>
  );
}
